import { ContentViewerActions } from '@/content-viewer/store/slice'
import { ViewerOpenContextKind, type ViewerOpenContext } from '@/content-viewer/store/schemas'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { FullMetadata } from 'firebase/storage'
import { useCallback, useContext } from 'react'
import { AuthContext } from '@/context/auth-context'
import { getFileMetadataFromGCS } from '../utils/files-utils'
import { buildLegacyFileViewerSessionFromMetadata } from '../utils/build-legacy-file-viewer-session'

type ViewFileContext = Extract<ViewerOpenContext, { kind: ViewerOpenContextKind.DRIVE_FILE | ViewerOpenContextKind.SELECTED_FILE }>

type ViewFileOptions = {
  context?: ViewFileContext
}

/**
 * Hook for viewing files from File Drive
 *
 * Builds a legacy file viewer session from Firebase metadata.
 */
export function useViewFile() {
  const dispatch = useAppDispatch()
  const activeMatterId = useAppSelector((state) => state.mattersState.activeMatterId)
  const { user } = useContext(AuthContext)

  const viewFile = useCallback(
    async (file: FullMetadata | string, options: ViewFileOptions = {}) => {
      if (!user) {
        console.error('No user logged in')
        return
      }

      const fileMetadata: FullMetadata = typeof file === 'string' ? await getFileMetadataFromGCS(file) : file

      const context = options.context ?? {
        kind: ViewerOpenContextKind.DRIVE_FILE,
        ...(activeMatterId ? { matterId: activeMatterId } : {}),
      }
      const session = buildLegacyFileViewerSessionFromMetadata(fileMetadata, context)
      if (!session) {
        console.error('File preview is not supported:', fileMetadata.name)
        return
      }

      dispatch(
        ContentViewerActions.setContentViewerSession({
          session,
        })
      )
    },
    [activeMatterId, dispatch, user]
  )

  return viewFile
}
